import SearchableDropdown from 'components/common/SearchableDropdown';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useAddDiscountModalStore } from 'stores/useAddDiscountModalStore';
import { DiscountModalTypeLabelMapping } from '../../types';

/**
 * Props for the ReasonForDiscount component.
 */
interface ReasonForDiscountProps {
  /**
   * List of reasons for discount.
   */
  reasons: string[];

  isBundle?: boolean;
}

/**
 * Component for selecting reason for discount.
 * @param reasons - List of reasons for discount.
 * @returns JSX.Element
 */
export const ReasonForDiscount: FC<ReasonForDiscountProps> = ({ reasons, isBundle }) => {
  const discountReason = useAddDiscountModalStore(state => state.specialDiscount.reason);
  const setDiscountReason = useAddDiscountModalStore(state => state.specialDiscount.setReason);
  const discountModalType = useAddDiscountModalStore(state => state.discountModalType);
  const mountedRef = useRef(false);
  const isInitial = useRef(true);
  const currentOrder = useAddDiscountModalStore(state => state.currentOrder);
  const selectedReason = useMemo(() => discountReason.value, [discountReason]);
  const hasError = useMemo(() => discountReason.error, [discountReason]);
  const currentOrderNumber = useMemo(() => currentOrder?.orderNumber, [currentOrder]);
  const label = useMemo(() => {
    const labelType = DiscountModalTypeLabelMapping[discountModalType];
    return `Reason for ${labelType}`;
  }, [discountModalType]);

  /**
   * Set default selected discount reason if not selected once component is mounted.
   */
  useEffect(() => {
    if (selectedReason || mountedRef.current) return;
    setDiscountReason({ value: '', error: false });

    mountedRef.current = true;
  }, [selectedReason, reasons, setDiscountReason]);

  useEffect(() => {
    /**
     * If this isn't the initial render and no order is selected,
     * set an error on the discount reason.
     */
    if (!isInitial.current && !currentOrderNumber) {
      setDiscountReason({ error: true });
    } else if (currentOrderNumber && selectedReason) {
      /**
       * If an order is selected after selecting a reason,
       * reset the error state.
       */
      setDiscountReason({ error: false });
    }
    // Mark that the initial render has occurred
    isInitial.current = false;
  }, [currentOrderNumber, selectedReason, setDiscountReason]);

  /**
   * Determines the appropriate error message based on the discount reason error state and order details.
   *
   * - If hasError is true and an order number exists, prompts the user to select a discount reason.
   * - If hasError is true, no order number is present, and it's a bundle case, prompts the user to select a case first.
   * - Otherwise, returns an empty string (no error).
   */
  const errorMessage =
    hasError && currentOrderNumber
      ? 'Please select a reason for discount'
      : hasError && !currentOrderNumber && isBundle
      ? 'Please select a case first.'
      : '';
  return (
    <div>
      <SearchableDropdown
        label={label}
        options={reasons}
        onSelectedChange={value => {
          setDiscountReason({ value, error: !currentOrderNumber });
        }}
        selected={selectedReason}
        id="discountFor"
        isRequired
        dataQa="reason-for-discount"
        errorMessage={errorMessage}
        placeholder="Select"
        isAbleToReset
      />
    </div>
  );
};
